import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { FORM, SECTIONS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Healthy Country Plan",
  "Indigenous Protected Area (IPA)",
  "Ranger program",
  "School / Education resource",
  "Restoration / Seed collection",
  "Carbon / Investment mapping",
  "Other",
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    organisation: "",
    email: "",
    phone: "",
    project_type: "",
    region: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and project details.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSuccess(true);
      toast.success("Thank you — your enquiry has been received.");
      setForm({
        name: "",
        organisation: "",
        email: "",
        phone: "",
        project_type: "",
        region: "",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || "Invalid input").join(", ")
        : typeof detail === "string"
        ? detail
        : "Could not send enquiry. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={SECTIONS.contact}
      className="relative py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — context */}
          <div className="lg:col-span-5">
            <div className="bm-eyebrow mb-6">Final · Start the journey</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
              Start a Bush <br /> Mapping Project
            </h2>
            <p className="mt-6 text-[#584D45] text-lg leading-relaxed max-w-md">
              Whether you are developing a Healthy Country Plan, Indigenous
              Protected Area, ranger program or educational resource, Bush
              Maps can help bring Country to life.
            </p>

            <div className="mt-10 p-6 bg-[#F3EFEA] border-l-2 border-[#A95A3F]">
              <div className="font-serif-display text-2xl text-[#2D2621]">
                William Martin
              </div>
              <div className="text-[#7A6C63] text-sm tracking-wide mt-1">
                <a
                  href="https://www.seedkeepers.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#A95A3F] transition-colors"
                >
                  SeedKeepers · Caring for Country
                </a>
              </div>
              <div className="mt-5 space-y-3 text-[#2D2621]">
                <a
                  href="mailto:william@bushmaps.com.au"
                  className="flex items-center gap-3 hover:text-[#A95A3F] transition-colors"
                >
                  <Mail size={16} className="text-[#A95A3F]" />
                  william@bushmaps.com.au
                </a>
                <div className="flex items-center gap-3 text-[#584D45]">
                  <MapPin size={16} className="text-[#A95A3F]" />
                  Working on Country across Australia
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            {success ? (
              <div
                data-testid={FORM.success}
                className="bg-[#2C3C2B] text-[#FDFBF7] p-12"
              >
                <div className="bm-eyebrow text-[#E9C7A8] mb-4">
                  Thank you
                </div>
                <h3 className="font-serif-display text-4xl leading-tight">
                  Your enquiry is with us.
                </h3>
                <p className="mt-4 text-[#D7CFC0] leading-relaxed max-w-xl">
                  William will be in touch within 2 business days to arrange a
                  Discovery Call. A copy of your enquiry has been sent to your
                  email.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="bm-btn-secondary mt-8"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid={FORM.form}
                className="bg-[#FDFBF7] border border-[#E2DACF] p-8 md:p-12"
              >
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label="Your name *">
                    <input
                      data-testid={FORM.name}
                      className="bm-input"
                      placeholder="Full name"
                      value={form.name}
                      onChange={update("name")}
                      required
                    />
                  </Field>
                  <Field label="Organisation">
                    <input
                      data-testid={FORM.organisation}
                      className="bm-input"
                      placeholder="Ranger group, council, etc."
                      value={form.organisation}
                      onChange={update("organisation")}
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      data-testid={FORM.email}
                      type="email"
                      className="bm-input"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={update("email")}
                      required
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      data-testid={FORM.phone}
                      className="bm-input"
                      placeholder="Optional"
                      value={form.phone}
                      onChange={update("phone")}
                    />
                  </Field>
                  <Field label="Project type">
                    <select
                      data-testid={FORM.projectType}
                      className="bm-input bg-transparent"
                      value={form.project_type}
                      onChange={update("project_type")}
                    >
                      <option value="">Select one (optional)</option>
                      {PROJECT_TYPES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Region / Country">
                    <input
                      data-testid={FORM.region}
                      className="bm-input"
                      placeholder="e.g. Cape York, Wet Tropics, Gulf"
                      value={form.region}
                      onChange={update("region")}
                    />
                  </Field>
                </div>

                <div className="mt-8">
                  <Field label="Tell us about your project *">
                    <textarea
                      data-testid={FORM.message}
                      className="bm-input min-h-[140px] resize-y"
                      placeholder="Aspirations, scope, timeline, who you're working with…"
                      value={form.message}
                      onChange={update("message")}
                      required
                    />
                  </Field>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <p className="text-xs text-[#7A6C63] max-w-md leading-relaxed">
                    Your information is shared only with William Martin. We
                    respect cultural protocols and never share community
                    information without consent.
                  </p>
                  <button
                    type="submit"
                    data-testid={FORM.submit}
                    disabled={loading}
                    className="bm-btn-primary disabled:opacity-60"
                  >
                    {loading ? "Sending…" : "Book a Discovery Call"}{" "}
                    {!loading && <ArrowRight size={16} />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.24em] uppercase text-[#7A6C63] mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
